/**
 * Sending a Draper chat message, engine-aware.
 *
 *  - gemini         : POST /api/workspaces/:id/draper-unified-chat (unchanged built-in path; replies synchronously).
 *  - hybrid         : same route and the same instant reply as gemini. The server's Gemini turn may hand production work to
 *                     the Claude team (a `work` job); that job is not part of the chat request and is not tracked here
 *                     (the Draper page shows it as a "Work in progress" strip and the finished note arrives as a normal message).
 *  - claude_worker  : POST /api/conversations/:id/chat, which queues the message for the Claude worker and
 *                     answers {queued, jobId, conversationId}. The unified Draper thread has no conversation row
 *                     of its own, so the main thread uses a dedicated "Draper chat" conversation that is found
 *                     (or created) on first use and cached per workspace. The server mirrors the user turn and the
 *                     worker's reply into the unified Draper thread, so the Draper page shows both over its SSE.
 *
 * Project threads are not routed through the worker queue; they keep using draper-unified-chat.
 */
import { queuesChat, type AiEngine } from "@/lib/ai-engine";

const DRAPER_CONVERSATION_TITLE = "Draper chat";
const JSON_HEADERS = { "Content-Type": "application/json" };

const cacheKey = (workspaceId: number) => `drpr-draper-conv-${workspaceId}`;

function readCachedConversationId(workspaceId: number): number | null {
  try {
    const n = Number(localStorage.getItem(cacheKey(workspaceId)));
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeCachedConversationId(workspaceId: number, id: number | null): void {
  try {
    if (id === null) localStorage.removeItem(cacheKey(workspaceId));
    else localStorage.setItem(cacheKey(workspaceId), String(id));
  } catch {
    /* storage unavailable — we just look the conversation up again next time */
  }
}

// Collapses concurrent first-sends into one lookup/create so we never create duplicate conversations.
const inflightLookups = new Map<number, Promise<number>>();

function findOrCreateConversation(workspaceId: number): Promise<number> {
  const existing = inflightLookups.get(workspaceId);
  if (existing) return existing;

  const lookup = (async () => {
    const listRes = await fetch(`/api/workspaces/${workspaceId}/conversations`, { credentials: "include" });
    if (!listRes.ok) throw new Error(`Couldn't load conversations (HTTP ${listRes.status})`);
    const list = (await listRes.json()) as Array<{ id: number; title?: string | null }>;
    const found = list.find((c) => c.title === DRAPER_CONVERSATION_TITLE);
    if (found) return found.id;

    const createRes = await fetch(`/api/workspaces/${workspaceId}/conversations`, {
      method: "POST",
      credentials: "include",
      headers: JSON_HEADERS,
      body: JSON.stringify({ title: DRAPER_CONVERSATION_TITLE }),
    });
    if (!createRes.ok) throw new Error(`Couldn't create the Draper conversation (HTTP ${createRes.status})`);
    return ((await createRes.json()) as { id: number }).id;
  })().finally(() => { inflightLookups.delete(workspaceId); });

  inflightLookups.set(workspaceId, lookup);
  return lookup;
}

/** @Peggy / @Pete / @Rogers / @Harry in the text selects that specialist (the chat route's agentTarget enum). */
function mentionedAgent(text: string): string | undefined {
  const m = text.match(/@(Peggy|Pete|Rogers|Harry)\b/i);
  return m ? m[1].toLowerCase() : undefined;
}

async function postConversationChat(workspaceId: number, message: string): Promise<Response> {
  const agentTarget = mentionedAgent(message);
  const post = (conversationId: number) =>
    fetch(`/api/conversations/${conversationId}/chat`, {
      method: "POST",
      credentials: "include",
      headers: JSON_HEADERS,
      body: JSON.stringify({ message, ...(agentTarget ? { agentTarget } : {}) }),
    });

  let conversationId = readCachedConversationId(workspaceId);
  if (conversationId === null) {
    conversationId = await findOrCreateConversation(workspaceId);
    writeCachedConversationId(workspaceId, conversationId);
  }

  const res = await post(conversationId);
  if (res.status !== 404) return res;

  // The cached conversation no longer exists — look it up (or create it) again and retry once.
  writeCachedConversationId(workspaceId, null);
  conversationId = await findOrCreateConversation(workspaceId);
  writeCachedConversationId(workspaceId, conversationId);
  return post(conversationId);
}

export interface PostDraperChatArgs {
  workspaceId: number;
  engine: AiEngine;
  /** The user's message text. */
  message: string;
  /** Full request body for the built-in draper-unified-chat route. */
  unifiedBody: Record<string, unknown>;
  /** Set for project threads; these are never routed through the worker queue. */
  projectId?: number | null;
}

/**
 * Sends one chat message and returns the raw Response (callers keep their own error handling).
 * `viaWorker` is true when the message went to the worker-queue route, in which case the response
 * is expected to be a QueuedChatResponse.
 */
export async function postDraperChat(args: PostDraperChatArgs): Promise<{ res: Response; viaWorker: boolean }> {
  const viaWorker = queuesChat(args.engine) && !args.projectId;
  if (viaWorker) {
    return { res: await postConversationChat(args.workspaceId, args.message), viaWorker };
  }
  const res = await fetch(`/api/workspaces/${args.workspaceId}/draper-unified-chat`, {
    method: "POST",
    credentials: "include",
    headers: JSON_HEADERS,
    body: JSON.stringify(args.unifiedBody),
  });
  return { res, viaWorker };
}
